import { useState, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  Clock,
  ThermometerSun,
  Palmtree,
  CalendarDays,
  Mail,
  BookOpen,
  ShoppingCart,
  Package,
  Building2,
  BarChart3,
  UserCog,
  Settings,
  LogOut,
  Bot,
  AlertTriangle,
  ChevronDown,
  Wrench,
  Calculator,
  Receipt,
  FileOutput,
  FolderArchive,
  CalendarClock,
  Bug,
  DraftingCompass,
  ExternalLink,
} from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { officeMessages as messagesApi, defects as defectsApi, orders as ordersApi, fristen as fristenApi, equipment as equipmentApi } from '@/utils/api'
import { cn } from '@/lib/utils'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'

interface SubItem {
  path: string
  icon: React.ComponentType<{ className?: string }>
  label: string
  /** eigene Seite im neuen Tab (ohne Sidebar), z. B. der DWG-Editor unter /dwg/ */
  newTab?: boolean
}

const baustelleSubItems: SubItem[] = [
  { path: '/admin/bauvorhaben', icon: Building2, label: 'Bauvorhaben' },
  { path: '/admin/bautagebuch', icon: BookOpen, label: 'Bautagebücher' },
  { path: '/admin/bestellungen', icon: ShoppingCart, label: 'Bestellungen' },
  { path: '/admin/produkte', icon: Package, label: 'Produkte' },
  { path: '/admin/geraete', icon: Wrench, label: 'Geräte' },
]

const mitarbeiterSubItems: SubItem[] = [
  { path: '/admin/mitarbeiter', icon: Users, label: 'Übersicht' },
  { path: '/admin/zeiterfassung', icon: Clock, label: 'Zeiterfassung' },
  { path: '/admin/krankmeldungen', icon: ThermometerSun, label: 'Krankmeldungen' },
  { path: '/admin/urlaub', icon: Palmtree, label: 'Urlaub' },
  { path: '/admin/stundenzettel', icon: CalendarDays, label: 'Stundenzettel' },
]

const backofficeSubItems: SubItem[] = [
  { path: '/admin/buchhaltung', icon: Receipt, label: 'Buchhaltung' },
  { path: '/admin/ausgangsrechnungen', icon: FileOutput, label: 'Ausgangsrechnungen' },
  { path: '/admin/dokumentenablage', icon: FolderArchive, label: 'Dokumentenablage' },
  { path: '/admin/fristen', icon: CalendarClock, label: 'Fristen' },
  { path: '/admin/kalkulation', icon: Calculator, label: 'Kalkulation' },
  { path: '/dwg/', icon: DraftingCompass, label: 'DWG-Editor', newTab: true },
]

function NavGroup({
  label,
  icon: Icon,
  items,
  defaultOpen,
  badges,
}: {
  label: string
  icon: React.ComponentType<{ className?: string }>
  items: SubItem[]
  defaultOpen: boolean
  badges?: Record<string, number>
}) {
  const [open, setOpen] = useState(defaultOpen)
  const location = useLocation()
  const isActive = items.some(item => !item.newTab && location.pathname.startsWith(item.path))
  const totalBadge = badges ? Object.values(badges).reduce((a, b) => a + (b || 0), 0) : 0

  useEffect(() => {
    if (isActive) setOpen(true)
  }, [location.pathname, isActive])

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        className={cn(
          'flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground mx-2',
          isActive && 'text-sidebar-foreground'
        )}
      >
        <Icon className="h-5 w-5 shrink-0" />
        <span className="flex-1 text-left">{label}</span>
        {/* Punkt am eingeklappten Gruppen-Header (Bugtracker #98) */}
        {!open && totalBadge > 0 && (
          <span className="shrink-0 rounded-full bg-amber-500 px-1.5 py-0.5 text-xs font-medium text-white">
            {totalBadge}
          </span>
        )}
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-4 border-l border-sidebar-border pl-0">
          {items.map((sub) => {
            const subBadge = badges?.[sub.path] || 0
            if (sub.newTab) {
              return (
                <a
                  key={sub.path}
                  href={sub.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`${sub.label} (neuer Tab)`}
                  className="flex items-center gap-2 rounded-r-lg py-2 pl-4 pr-3 mr-2 text-sm transition-colors text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                >
                  <sub.icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate">{sub.label}</span>
                  <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-60" />
                </a>
              )
            }
            return (
            <NavLink
              key={sub.path}
              to={sub.path}
              end
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-r-lg py-2 pl-4 pr-3 mr-2 text-sm transition-colors',
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-foreground'
                    : 'text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
                )
              }
            >
              <sub.icon className="h-4 w-4 shrink-0" />
              <span className="flex-1 truncate">{sub.label}</span>
              {subBadge > 0 && (
                <span className="ml-auto shrink-0 rounded-full bg-amber-500 px-1.5 py-0.5 text-xs font-medium text-white">
                  {subBadge}
                </span>
              )}
            </NavLink>
            )
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

export function AppSidebar({ onClose }: { onClose?: () => void }) {
  const { user, logout } = useAuth()
  const [unreadCount, setUnreadCount] = useState(0)
  const [rejectionCount, setRejectionCount] = useState(0)
  const [orderCount, setOrderCount] = useState(0)
  const [fristenCount, setFristenCount] = useState(0)
  const [equipmentDecisionCount, setEquipmentDecisionCount] = useState(0)
  const location = useLocation()

  const baustellePaths = baustelleSubItems.map(i => i.path)
  const mitarbeiterPaths = mitarbeiterSubItems.map(i => i.path)
  const backofficePaths = backofficeSubItems.map(i => i.path)

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const res = await messagesApi.getUnreadCount()
        setUnreadCount(res.data.count || 0)
      } catch {
        // Silently fail
      }
    }
    fetchUnreadCount()
    const interval = setInterval(fetchUnreadCount, 60000)
    return () => clearInterval(interval)
  }, [])

  // Bugtracker #93: abgelehnte, noch nicht gesehene Mängel → roter Punkt an "Mängel"
  useEffect(() => {
    const fetchRejections = async () => {
      try {
        const res = await defectsApi.getStats()
        setRejectionCount(res.data.unseen_rejections || 0)
      } catch {
        // Silently fail
      }
    }
    fetchRejections()
    const interval = setInterval(fetchRejections, 60000)
    return () => clearInterval(interval)
  }, [location.pathname])

  // Bugtracker #98: neue (ungesehene) Bestellungen → gelber Punkt an "Bestellungen"
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await ordersApi.getStats()
        setOrderCount(res.data.unseen_count || 0)
      } catch {
        // Silently fail
      }
    }
    fetchOrders()
    const interval = setInterval(fetchOrders, 60000)
    return () => clearInterval(interval)
  }, [location.pathname])

  // Geräte: offene Defekte + zu bestätigende Aussonderungen → Badge an "Geräte"
  useEffect(() => {
    const fetchEquipment = async () => {
      try {
        const res = await equipmentApi.getStats()
        setEquipmentDecisionCount((res.data.openDefects || 0) + (res.data.disposalPending || 0))
      } catch (err) {
        console.error('Geräte-Badge laden fehlgeschlagen:', err)
      }
    }
    fetchEquipment()
    const interval = setInterval(fetchEquipment, 60000)
    return () => clearInterval(interval)
  }, [location.pathname])

  // Fristen: ablaufende/abgelaufene Dokumente → gelber Punkt an "Fristen"
  useEffect(() => {
    const fetchFristen = async () => {
      try {
        const res = await fristenApi.getStats()
        setFristenCount(res.data.warning_count || 0)
      } catch {
        // Silently fail
      }
    }
    fetchFristen()
    const interval = setInterval(fetchFristen, 60000)
    return () => clearInterval(interval)
  }, [location.pathname])

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm transition-colors mx-2',
      isActive
        ? 'bg-sidebar-accent text-sidebar-foreground'
        : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground'
    )

  return (
    <aside className="flex h-full w-64 flex-col bg-sidebar text-sidebar-foreground overflow-x-hidden">
      {/* Logo */}
      <div className="border-b border-sidebar-border p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1 rounded-lg bg-white p-3 flex items-center justify-center">
            <img src="/logo.jpg" alt="COMPEER" className="h-10 w-auto object-contain" />
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="lg:hidden ml-2 text-sidebar-foreground/60 hover:text-sidebar-foreground p-1 rounded"
            >
              <span className="sr-only">Close</span>
              &times;
            </button>
          )}
        </div>
        <p className="mt-2 text-center text-xs text-sidebar-foreground/60">Bau-Management</p>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className="space-y-1">
          <NavLink to="/admin" end className={navLinkClass}>
            <LayoutDashboard className="h-5 w-5 shrink-0" />
            <span>Dashboard</span>
          </NavLink>

          <NavLink to="/admin/maengel" className={navLinkClass}>
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <span className="flex-1">Mängel</span>
            {rejectionCount > 0 && (
              <span
                className="ml-auto shrink-0 rounded-full bg-red-600 px-1.5 py-0.5 text-xs font-medium text-white"
                title={`${rejectionCount} abgelehnte${rejectionCount === 1 ? 'r Mangel' : ' Mängel'}`}
              >
                {rejectionCount}
              </span>
            )}
          </NavLink>

          <NavGroup
            label="Baustelle"
            icon={Building2}
            items={baustelleSubItems}
            defaultOpen={baustellePaths.some(p => location.pathname.startsWith(p))}
            badges={{ '/admin/bestellungen': orderCount, '/admin/geraete': equipmentDecisionCount }}
          />

          <NavGroup
            label="Mitarbeiter"
            icon={Users}
            items={mitarbeiterSubItems}
            defaultOpen={mitarbeiterPaths.some(p => location.pathname.startsWith(p))}
          />

          <NavGroup
            label="Backoffice"
            icon={Receipt}
            items={backofficeSubItems}
            defaultOpen={backofficePaths.some(p => location.pathname.startsWith(p))}
            badges={{ '/admin/fristen': fristenCount }}
          />

          <NavLink to="/admin/nachrichten" className={navLinkClass}>
            <Mail className="h-5 w-5 shrink-0" />
            <span className="flex-1">Nachrichten</span>
            {unreadCount > 0 && (
              <span className="ml-auto shrink-0 rounded-full bg-accent-500 px-1.5 py-0.5 text-xs font-medium text-white">
                {unreadCount}
              </span>
            )}
          </NavLink>

          <NavLink to="/admin/berichte" className={navLinkClass}>
            <BarChart3 className="h-5 w-5 shrink-0" />
            <span>Berichte</span>
          </NavLink>

          <NavLink to="/admin/ki" className={navLinkClass}>
            <Bot className="h-5 w-5 shrink-0" />
            <span>K.I.</span>
          </NavLink>

          <NavLink to="/admin/bug-tracker" className={navLinkClass}>
            <Bug className="h-5 w-5 shrink-0" />
            <span>Bug Tracker</span>
          </NavLink>
        </nav>
      </ScrollArea>

      {/* User info & Bottom actions */}
      <div className="border-t border-sidebar-border px-3 py-3">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-sidebar-foreground">
              {user?.display_name}
            </p>
            <p className="truncate text-xs text-sidebar-foreground/60">
              {user?.role === 'admin' ? 'Administrator' : 'Bauleiter'}
            </p>
          </div>
          <div className="flex shrink-0 items-center">
            <NavLink
              to="/admin/benutzerverwaltung"
              className={({ isActive }) =>
                cn(
                  'rounded p-1.5 transition-colors',
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-foreground'
                    : 'text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
                )
              }
              title="Benutzerverwaltung"
            >
              <UserCog className="h-4 w-4" />
            </NavLink>
            <NavLink
              to="/admin/einstellungen"
              className={({ isActive }) =>
                cn(
                  'rounded p-1.5 transition-colors',
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-foreground'
                    : 'text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
                )
              }
              title="Einstellungen"
            >
              <Settings className="h-4 w-4" />
            </NavLink>
            <button
              onClick={logout}
              className="rounded p-1.5 text-sidebar-foreground/60 transition-colors hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
              title="Abmelden"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </aside>
  )
}
